import { useEffect } from 'react';
import tw, { TwStyle } from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Modal } from '@components/Modal';
import { InviteMemberForm } from '@account_module/components/onboarding/InviteMemberForm';
import { Header } from '@account_module/components/Header';
import { OnboardSidebar } from '@account_module/components/onboarding/MegaphoneOnboardSidebar';
import { OnboardChecklist } from '@account_module/components/onboarding/MegaphoneOnboardChecklist';
import { OnboardWizard } from '@account_module/components/onboarding/MegaphoneOnboardWizard';
import { useMegaphoneOnboardContext } from '@account_module/context/MegaphoneOnboardContext';
import { useIntegration } from '@account_module/hooks/useIntegrations';
import { MEGAPHONE_ONBOARDING_STEP_KEYS } from '@account_module/constants/onBoardingKeys';
import { IntegrationResponseType } from '@account_module/types/Integration';
import { OnboardingStepType } from '@account_module/types/Onboarding';
import { useOrganizations } from '@account_module/hooks/useOrganizations';

const HEADER_HEIGHT = '61';
const onboardSteps: OnboardingStepType[] = Object.values(MEGAPHONE_ONBOARDING_STEP_KEYS);

const FlexContainer = tw.div`w-full h-[calc(100vh - ${HEADER_HEIGHT}px)] p-[24px] flex justify-between bg-secondary`;
const FlexItem = tw.div`relative`;

const closedStyles = tw`w-0 min-w-0 overflow-hidden transition-all duration-150 px-0` as TwStyle;
const openStyles = tw`w-[321px] min-w-[321px]` as TwStyle;

export const MegaphoneOnboardPage = () => {
  const { t } = useTranslation();
  const {
    setToken,
    setCurrentStep,
    setIntegrationId,
    setOrganizationId,
    setNetworksToInclude,
    setNetworksToExclude,
    setPersistedProgressStep,
  } = useMegaphoneOnboardContext();
  const { data: organizations } = useOrganizations();
  const { data: integration } = useIntegration();

  const { isSidebarVisible, isInviteModalVisible, setIsInviteModalVisible } =
    useMegaphoneOnboardContext();

  const setPreviousOnboardingState = (integration: IntegrationResponseType) => {
    setToken(integration.secret_token);
    setIntegrationId(integration.id);
    setOrganizationId(integration.integration_organization_id);
    setCurrentStep(onboardSteps[integration.progress_step]);
    setPersistedProgressStep(integration.progress_step);

    if (integration.include_networks) {
      integration.include_networks
        .split(',')
        .forEach((network: string) => setNetworksToInclude(network));
    }

    if (integration.exclude_networks) {
      integration.exclude_networks
        .split(',')
        .forEach((network: string) => setNetworksToExclude(network));
    }
  };

  useEffect(() => {
    if (integration.id) {
      setPreviousOnboardingState(integration as IntegrationResponseType);
    }
  }, [integration]);

  return (
    <>
      <Header showUserTableControler={false} showBreadchrumbs={false} label={t('common.beta')} />
      <FlexContainer>
        <FlexItem>
          <OnboardChecklist />
        </FlexItem>
        <FlexItem tw='grow min-w-[400px]'>
          <OnboardWizard />
        </FlexItem>
        <FlexItem css={[closedStyles, isSidebarVisible && openStyles]}>
          <OnboardSidebar />
        </FlexItem>
      </FlexContainer>

      {isInviteModalVisible && (
        <Modal
          onClose={() => setIsInviteModalVisible(false)}
          overlayStyles={tw`bg-[rgba(0, 0, 0, 0.25)]`}>
          <InviteMemberForm
            organizationId={organizations?.[0]?.id ?? integration?.organization_id}
          />
        </Modal>
      )}
    </>
  );
};
